import { AdminTableItem } from 'components/molecules/AdminTable';
import { ActionRequiredGoal, AdvisorConsoleStoreFilters } from './types';
import { StatusVariant } from 'components/molecules/AdminTable/Status';

export const getNumberOfPages = (itemsLength: number, itemsPerPage: number) => {
    return Math.ceil(itemsLength / itemsPerPage);
};

export const getItemsByPage = ({
    dataItems,
    itemsPerPage,
    page
}: {
    dataItems: AdminTableItem[];
    itemsPerPage: number;
    page: number;
}) => {
    return dataItems?.slice((page - 1) * itemsPerPage, page * itemsPerPage) ?? [];
};

export const filterDataItems = ({
    dataItems,
    filters
}: {
    dataItems: AdminTableItem[];
    filters: AdvisorConsoleStoreFilters;
}) => {
    const { client, riskProfile, priority, type } = filters || {};

    return dataItems?.filter(({ clientGoalName, riskGoalPriority, status, subItems = [] }) => {
        const validations: boolean[] = [];

        if (priority) {
            validations.push(
                subItems.some(({ goalPriority }) => goalPriority?.toUpperCase() === priority.toUpperCase())
            );
        }

        if (type) {
            validations.push(subItems.some(({ goalType }) => goalType?.toUpperCase() === type.toUpperCase()));
        }

        if (riskProfile) {
            validations.push(riskProfile?.toUpperCase() === riskGoalPriority?.toUpperCase());
        }

        if (filters.status) {
            validations.push(filters.status?.toUpperCase() === status?.toUpperCase());
        }

        if (client?.value) {
            const isSameClient = clientGoalName?.toUpperCase() === filters.client?.value;

            validations.push(isSameClient);
        }

        return !validations.includes(false);
    });
};

// Helper to strip leading zeros (e.g., 078 -> 78). Keeps single zero if the entire string was zeros.
export const normalizeAge = (age: any, fallback: string): string => {
    if (age === undefined || age === null || age === '') return fallback;
    const str = String(age).trim();
    const stripped = str.replace(/^0+/, '');
    return stripped === '' ? '0' : stripped;
};

const isValueEmpty = (value: any): boolean => {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
};

export const isRecordDataValid = (
    record: any,
    mandatoryFields: any,
    needToCheckIncomeAmount = false,
    needToCheckAccountConditions = false
) => {
    // 1. Mandatory Fields Check
    for (const key of mandatoryFields) {
        const value = record[key];
        if (isValueEmpty(value)) {
            return false;
        }
    }
    if (needToCheckIncomeAmount) {
        // 2. Conditional Check: Income Amount Check
        if (incomeAmountValidity(record?.amount)) {
            return false;
        }
    }
    if (needToCheckAccountConditions) {
        // 2. Conditional Check: Recurring Contributions
        if (record?.recurringContributions > 0) {
            if (
                isValueEmpty(record?.frequency) ||
                isValueEmpty(record?.recurringContributionsStartDate) ||
                isValueEmpty(record?.recurringContributionsEndDate)
            ) {
                return false;
            }
        }
        // 3. Conditional Check: One-Time Future Amount
        if (record?.oneTimeFutureAmount !== undefined) {
            if (isValueEmpty(record.fundingYear)) {
                return false;
            }
        }
        // 3. Conditional Check: Account Amount Check
        if (accountAmountValidity(record?.amount)) {
            return false;
        }
    }
    return true;
};

const incomeAmountValidity = (amount: number): boolean => {
    return amount <= 0;
};

const accountAmountValidity = (amount: number) => {
    return amount === undefined;
};

export const determineLastName = (firstName: any = '', lastName: any = '') => {
    if (!lastName || lastName.trim() === '') {
        // lastName is absent or empty, fall back to firstName logic
        const spaceIndex = firstName?.indexOf(' ');
        if (spaceIndex !== -1) {
            return firstName.substring(spaceIndex + 1).trim();
        } else {
            return '';
        }
    }
    // 1. If coClient.lastName is present and not empty, use it.
    return lastName;
};

export const calculateEquityFixedIncome = ({ portfolios, recommendedPortfolioId }: any) => {
    const portfolio = portfolios?.find((_sPort: any) => _sPort.portfolioId === recommendedPortfolioId);

    return (
        portfolio?.assets
            ?.map(({ totalAllocatedvalue }: { totalAllocatedvalue: number }) => {
                return totalAllocatedvalue || 0;
            })
            .join(' / ') || ''
    );
};

export const getMaskedStatus = (status: StatusVariant, isFlow2: boolean = false) => {
    if (isFlow2 && status === 'incomplete') {
        return 'draft';
    }
    return status;
};

// AEX-3218/AEX-3570: a goal notification is "active" when it is OPEN, not dismissed and scoped to a
// goal. On the real getproposals payload scope/event_type/goal_name/target_date live inside
// `attributes` (goal_id/status/dismissed/severity are also mirrored at the top level) — read both.
const isActiveGoalNotification = (notification: any): boolean => {
    const scope = notification?.attributes?.scope ?? notification?.scope;
    return notification?.status === 'OPEN' && !notification?.dismissed && scope === 'goal';
};

const getNotificationGoalId = (notification: any): string | undefined =>
    notification?.goal_id ?? notification?.attributes?.goal_id;

// AEX-3218: distinct goals in a plan that currently need advisor review, enriched with the goal
// details (name/term/amount/target date) the "Review Goals" modal renders. Multiple notifications
// for the same goal collapse to one entry. Callers gate display on the feature flag + flow2.
export const getActionRequiredGoals = (notifications: any[] = [], formFields: any[] = []): ActionRequiredGoal[] => {
    const byGoalId = new Map<string, ActionRequiredGoal>();
    (notifications ?? []).forEach((notification) => {
        const goalId = getNotificationGoalId(notification);
        if (!isActiveGoalNotification(notification) || !goalId || byGoalId.has(goalId)) {
            return;
        }
        const attributes = notification?.attributes ?? {};
        const goalData =
            (formFields ?? []).find((field) => (field?.goalId ?? field?.data?.goalId) === goalId)?.data ?? {};
        byGoalId.set(goalId, {
            goalId,
            goalName: attributes?.goal_name ?? goalData?.goalName ?? '',
            goalCategory: goalData?.goalCategory,
            targetAmount: goalData?.goalAmount != null ? Number(goalData.goalAmount) : undefined,
            targetDate: attributes?.target_date ?? goalData?.targetDate ?? goalData?.goalDate,
            eventType: attributes?.event_type,
            severity: notification?.severity ?? attributes?.severity,
            idempotencyKey: attributes?.idempotency_key,
            notification_id: notification?.notification_id,
            notification_status: notification?.status
        } as ActionRequiredGoal);
    });
    return Array.from(byGoalId.values());
};

// Number of distinct goals needing review — used to gate the Advisor Console action-required indicator.
export const getActionRequiredGoalCount = (notifications: any[] = []): number =>
    getActionRequiredGoals(notifications).length;
